import React, { useState, useRef } from 'react';
import { ProgressBar, Table, Badge } from 'react-bootstrap';
import styles from './ItemPricesCSVTool.module.css';
import ValidationFeedback from '../../components/ValidationFeedback/ValidationFeedback';
import { UpdateItemPrices, GetItemPrices } from '../../libs/GraphQL';

const VALIDATION_TYPE = {
  ERRORS: 'errors',
  WARNINGS: 'warnings',
  SUCCESS: 'success',
};

const isEmpty = (value) => {
  return value.trim() === '';
};

const ItemPricesCSVTool = () => {
  const [csvFile, setCsvFile] = useState(null);
  const [errors, setErrors] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [parsedData, setParsedData] = useState(null);
  const [rowErrors, setRowErrors] = useState({});
  const [invalidRows, setInvalidRows] = useState([]);
  const [changeStatus, setChangeStatus] = useState({});
  const [fetchingPrices, setFetchingPrices] = useState(false);
  const [rowErrorsFromBackend, setRowErrorsFromBackend] = useState([]);
  const [validating, setValidating] = useState(false);
  const csvFileRef = useRef(null);

  // Helper to get all errors for a row (frontend and backend)
  const getAllErrorsForRow = (row, rowIndex) => {
    const frontend = rowErrors[rowIndex] || [];
    const backendRow = rowErrorsFromBackend.find(
      (r) => r.schemaId === row.schemaId && r.item === row.item,
    );
    const backend = backendRow ? backendRow.errors : [];
    return { frontend, backend };
  };

  // Helper to determine if a row is invalid (frontend or backend errors)
  const isRowInvalid = (row, rowIndex) => {
    const { frontend, backend } = getAllErrorsForRow(row, rowIndex);
    return frontend.length > 0 || backend.length > 0;
  };

  const resetForm = () => {
    setCsvFile(null);
    setParsedData(null);
    setRowErrors({});
    setErrors([]);
    setInvalidRows([]);
    setChangeStatus({});
    setRowErrorsFromBackend([]);
    setValidating(false);
    if (csvFileRef.current) {
      csvFileRef.current.value = null;
    }
  };

  const validatePrice = (price) => {
    const numPrice = parseFloat(price);
    // Only allow numbers >= 0 and at most 2 decimal places
    return (
      !Number.isNaN(numPrice) &&
      numPrice >= 0 &&
      /^\d+(\.\d{1,2})?$/.test(price)
    );
  };

  const getDataFromCsv = (csvText) => {
    const rows = csvText
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line);
    const headers = rows.shift().split(',');
    return { headers, rows };
  };

  const fetchCurrentPrices = async (schemaIds) => {
    if (schemaIds.length === 0) return {};

    setFetchingPrices(true);
    try {
      const pricesMap = {};

      // Fetch prices for each schema
      const input = { schemaIds };
      let prices;
      try {
        prices = await GetItemPrices(input);
      } catch (err) {
        // If backend returns a field error, show it in the UI
        const backendMsg = err && err.message ? err.message : String(err);
        if (backendMsg.includes('not defined for input object type')) {
          setFeedback({
            type: VALIDATION_TYPE.ERRORS,
            data: [
              {
                title: 'Backend Schema Error',
                description: `The input sent to the backend is invalid: ${JSON.stringify(
                  input,
                )}.\n${backendMsg}`,
              },
            ],
          });
        }
        throw err;
      }
      // Build your pricesMap from all returned prices
      prices.forEach((priceData) => {
        const key = `${priceData.schemaId}|${priceData.item}`;
        pricesMap[key] = {
          price: priceData.price,
          exists: priceData.exists,
        };
      });

      return pricesMap;
    } catch (error) {
      console.error('Error fetching current prices:', error);
      if (!feedback) {
        setFeedback({
          type: VALIDATION_TYPE.ERRORS,
          data: [
            {
              title: 'Error Fetching Current Prices',
              description:
                'Failed to fetch current prices for comparison. Please try again.',
            },
          ],
        });
      }
      return {};
    } finally {
      setFetchingPrices(false);
    }
  };

  const comparePrices = (parsedRows, pricesMap) => {
    const newChangeStatus = {};

    parsedRows.forEach((row, rowIndex) => {
      if (!row.isValid) {
        newChangeStatus[rowIndex] = 'Invalid';
        return;
      }

      const key = `${row.schemaId}|${row.item}`;
      const currentPriceData = pricesMap[key];
      const csvPrice = parseFloat(row.price);

      if (!currentPriceData) {
        // Item doesn't exist in backend
        newChangeStatus[rowIndex] = 'Will Update';
      } else if (parseFloat(currentPriceData.price) !== csvPrice) {
        // Price is different - ensure both are numbers for comparison
        newChangeStatus[rowIndex] = 'Will Update';
      } else {
        // Price is the same
        newChangeStatus[rowIndex] = 'No Change';
      }
    });

    setChangeStatus(newChangeStatus);
    return newChangeStatus;
  };

  // Add a helper to call backend validation (simulate mutation with validateOnly or similar)
  const runBackendValidation = async (validRows) => {
    setValidating(true);
    if (!validRows.length) {
      setRowErrorsFromBackend([]);
      setValidating(false);
      return;
    }
    try {
      // Prepare input for backend validation (simulate, do not update)
      const input = validRows.map((row) => ({
        schemaId: row.schemaId,
        item: row.item,
        price: parseFloat(row.price),
      }));
      const result = await UpdateItemPrices(input);
      if (result.rowErrors) {
        setRowErrorsFromBackend(result.rowErrors);
      } else {
        setRowErrorsFromBackend([]);
      }
    } catch (error) {
      setRowErrorsFromBackend([]);
    } finally {
      setValidating(false);
    }
  };

  const validateCsvContent = (csvText) => {
    setFeedback(null);
    setErrors([]);
    setRowErrors({});
    setInvalidRows([]);

    const csvErrors = [];
    const warnings = [];
    const { headers, rows } = getDataFromCsv(csvText);
    const newRowErrors = {};
    const keyToRows = new Map(); // key: schemaId|item, value: array of {rowIndex, lineNumber}
    const parsedRowsTemp = [];
    const invalidRowsArr = [];

    if (!rows) {
      csvErrors.push({
        title: `Line 1`,
        description:
          'No rows found. Provide at least one row with schemaId, item, and price.',
      });
      setInvalidRows([]);
      return;
    }

    // Validate headers
    const requiredHeaders = ['schemaId', 'item', 'price'];
    const missingHeaders = requiredHeaders.filter(
      (header) => !headers.includes(header),
    );
    if (missingHeaders.length > 0) {
      csvErrors.push({
        title: 'Header Validation',
        description: `Missing required columns: ${missingHeaders.join(', ')}`,
      });
      setInvalidRows([]);
      return;
    }

    // Parse and validate each row (first pass: basic validation, collect keys)
    rows.forEach((row, rowIndex) => {
      const columns = row.split(',');
      const [schemaIdRaw, itemRaw, priceRaw] = columns;
      const schemaId = schemaIdRaw ? schemaIdRaw.trim() : '';
      const item = itemRaw ? itemRaw.trim() : '';
      const price = priceRaw ? priceRaw.trim() : '';
      const lineNumber = rowIndex + 2;
      const key = `${schemaId}|${item}`;
      let rowErrors = [];
      let errorReason = '';

      if (isEmpty(schemaId)) {
        rowErrors.push('schemaId cannot be empty');
        csvErrors.push({
          title: `Line ${lineNumber}`,
          description: 'schemaId cannot be empty.',
        });
        errorReason += 'schemaId cannot be empty; ';
      }
      if (isEmpty(item)) {
        rowErrors.push('item cannot be empty');
        csvErrors.push({
          title: `Line ${lineNumber}`,
          description: 'item cannot be empty.',
        });
        errorReason += 'item cannot be empty; ';
      } else if (!/^[A-Z0-9_]+$/.test(item)) {
        rowErrors.push(
          'item must be all uppercase letters, numbers, or underscores (no spaces or special characters)',
        );
        csvErrors.push({
          title: `Line ${lineNumber}`,
          description:
            'item must be all uppercase letters, numbers, or underscores (no spaces or special characters).',
        });
        errorReason +=
          'item must be all uppercase letters, numbers, or underscores; ';
      }
      if (!validatePrice(price)) {
        rowErrors.push(
          'price must be a valid number with at most 2 decimal places and greater than or equal to 0',
        );
        csvErrors.push({
          title: `Line ${lineNumber}`,
          description:
            'price must be a valid number with at most 2 decimal places and greater than or equal to 0.',
        });
        errorReason +=
          'price must be a valid number >= 0 and at most 2 decimal places';
      }

      // Collect all row indices for each key
      if (!isEmpty(schemaId) && !isEmpty(item)) {
        if (!keyToRows.has(key)) {
          keyToRows.set(key, []);
        }
        keyToRows.get(key).push({ rowIndex, lineNumber });
      }

      parsedRowsTemp.push({
        schemaId,
        item,
        price,
        lineNumber,
        rowErrors,
        errorReason,
      });
    });

    // Second pass: mark all duplicates
    keyToRows.forEach((rowInfos, key) => {
      if (rowInfos.length > 1) {
        // For each row with this key, mark as duplicate and reference other line numbers
        rowInfos.forEach(({ rowIndex, lineNumber }, idx) => {
          const otherLines = rowInfos
            .filter((info) => info.lineNumber !== lineNumber)
            .map((info) => info.lineNumber)
            .join(', ');
          const duplicateMsg = `Duplicate schemaId + item combination (also found in line${
            otherLines.includes(',') ? 's' : ''
          } ${otherLines})`;
          parsedRowsTemp[rowIndex].rowErrors.push(duplicateMsg);
          parsedRowsTemp[rowIndex].errorReason += duplicateMsg + '; ';
          // Add to csvErrors if not already present
          csvErrors.push({
            title: `Line ${lineNumber}`,
            description: duplicateMsg,
          });
        });
      }
    });

    // Build parsedRows and invalidRowsArr for UI
    const parsedRows = parsedRowsTemp.map((row, rowIndex) => {
      if (row.rowErrors.length > 0) {
        newRowErrors[rowIndex] = row.rowErrors;
        invalidRowsArr.push({
          schemaId: row.schemaId,
          item: row.item,
          price: row.price,
          lineNumber: row.lineNumber,
          errorReason: row.errorReason.trim(),
        });
      }
      return {
        schemaId: row.schemaId,
        item: row.item,
        price: row.price,
        lineNumber: row.lineNumber,
        isValid: row.rowErrors.length === 0,
      };
    });

    setRowErrors(newRowErrors);
    setErrors(csvErrors);
    setInvalidRows(invalidRowsArr);
    if (warnings.length) {
      setFeedback({ type: VALIDATION_TYPE.WARNINGS, data: warnings });
    } else {
      setFeedback(null);
    }

    // Fetch current prices and compare if we have valid rows
    const validRows = parsedRows.filter((row) => row.isValid);
    if (validRows.length > 0) {
      const uniqueSchemaIds = [
        ...new Set(validRows.map((row) => row.schemaId)),
      ];
      fetchCurrentPrices(uniqueSchemaIds).then((pricesMap) => {
        comparePrices(parsedRows, pricesMap);
        runBackendValidation(validRows).then(() => {
          setParsedData({ headers, rows: parsedRows });
        });
      });
    } else {
      setParsedData({ headers, rows: parsedRows });
    }
  };

  const getPricingUpdateInput = (csvText) => {
    const { rows } = getDataFromCsv(csvText);
    const updateRows = [];

    rows.forEach((row, rowIndex) => {
      const [schemaId, item, price] = row.split(',');
      const trimmedSchemaId = schemaId.trim();
      const trimmedItem = item.trim();
      const trimmedPrice = price.trim();

      // Only include rows that will actually update prices
      if (changeStatus[rowIndex] === 'Will Update') {
        updateRows.push({
          schemaId: trimmedSchemaId,
          item: trimmedItem,
          price: parseFloat(trimmedPrice),
        });
      }
    });

    return updateRows;
  };

  const submitHandler = (e) => {
    e.preventDefault();
    setFeedback(null);
    setRowErrorsFromBackend([]);
    if (!csvFile || errors.length) return;

    const reader = new FileReader();
    reader.onload = async function (event) {
      setLoading(true);
      setUploadProgress(0);
      try {
        const content = event.target.result;
        const input = getPricingUpdateInput(content);

        if (input.length === 0) {
          setLoading(false);
          setFeedback({
            type: VALIDATION_TYPE.WARNINGS,
            data: [
              {
                title: 'No Updates Needed',
                description:
                  'No rows will be updated. All valid rows have the same prices as the backend.',
              },
            ],
          });
          resetForm();
          return;
        }

        setUploadProgress(50);

        const result = await UpdateItemPrices(input);

        setUploadProgress(100);
        setLoading(false);

        // If backend returns errors, show error feedback and download invalid rows
        if (result.errors && result.errors.length > 0) {
          setFeedback({
            type: VALIDATION_TYPE.ERRORS,
            data: [
              {
                title: 'Update Failed',
                description: result.message || 'Some updates failed.',
                details: result.errors.join('\n'),
              },
            ],
          });
          // Find the affected rows from the backend error (e.g., missing SKUs or schemas)
          if (parsedData && parsedData.rows) {
            let backendInvalids = [];
            result.errors.forEach((err) => {
              // Detect missing item errors
              if (err.includes('VendItems table:')) {
                // Example: "Items not found in VendItems table: PESTO_BOWL_BOWL"
                const items = err
                  .split('VendItems table:')[1]
                  .trim()
                  .split(/,| /)
                  .filter(Boolean);
                backendInvalids = backendInvalids.concat(
                  parsedData.rows
                    .filter((row) => items.includes(row.item))
                    .map((row) => ({ ...row, errors: [err] })),
                );
              }
              // Detect missing schema errors
              else if (err.toLowerCase().includes('schema')) {
                // Try to extract schemaId from error message
                // Example: "Schema DOES_NOT_EXIST not found"
                const schemaMatch = err.match(/schema\s+([A-Z0-9_]+)/i);
                let schemaId = null;
                if (schemaMatch && schemaMatch[1]) {
                  schemaId = schemaMatch[1];
                } else {
                  const fallback = err.match(/[A-Z0-9_]{3,}/);
                  if (fallback) schemaId = fallback[0];
                }
                if (schemaId) {
                  backendInvalids = backendInvalids.concat(
                    parsedData.rows
                      .filter((row) => row.schemaId === schemaId)
                      .map((row) => ({ ...row, errors: [err] })),
                  );
                }
              }
            });
            setRowErrorsFromBackend(backendInvalids);
          }
          return;
        }

        // Show success feedback
        setFeedback({
          type: VALIDATION_TYPE.SUCCESS,
          data: [
            {
              title: 'Success',
              description:
                result.message ||
                `Successfully updated ${input.length} item prices.`,
            },
          ],
        });

        resetForm();
      } catch (error) {
        setUploadProgress(100);
        setLoading(false);

        // Show error feedback
        setFeedback({
          type: VALIDATION_TYPE.ERRORS,
          data: [
            {
              title: 'Update Failed',
              description:
                error.message ||
                'An error occurred while updating item prices.',
            },
          ],
        });
      }
    };
    reader.readAsText(csvFile);
  };

  const changeFileHandler = (e) => {
    const file = e.target.files[0];
    if (file) {
      setValidating(true);
      setFeedback(null);
      setErrors([]);
      setRowErrors({});
      setInvalidRows([]);
      setChangeStatus({});
      setParsedData(null);
      setRowErrorsFromBackend([]);

      const reader = new FileReader();
      reader.onload = function (event) {
        const content = event.target.result;
        validateCsvContent(content);
        setCsvFile(file);
      };
      reader.readAsText(file);
    }
  };

  // Helper to check if a row is backend invalid
  const isBackendInvalidRow = (row) => {
    return rowErrorsFromBackend.some(
      (bRow) => bRow.lineNumber === row.lineNumber && bRow.item === row.item,
    );
  };

  // Download invalid rows as CSV
  const downloadInvalidRows = () => {
    if (!invalidRows.length) return;
    const csvHeaders = [
      'schemaId',
      'item',
      'price',
      'lineNumber',
      'errorReason',
    ];
    const csvContent = [
      csvHeaders.join(','),
      ...invalidRows.map((row) =>
        [
          row.schemaId,
          row.item,
          row.price,
          row.lineNumber,
          row.errorReason.replace(/[,\n]/g, ' '),
        ].join(','),
      ),
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'invalid_rows.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Download all invalid rows (frontend and backend errors)
  const downloadAllInvalidRows = () => {
    if (!parsedData || !parsedData.rows) return;
    const csvHeaders = ['schemaId', 'item', 'price', 'lineNumber', 'errors'];
    const csvContent = [
      csvHeaders.join(','),
      ...parsedData.rows
        .map((row, rowIndex) => {
          const { frontend, backend } = getAllErrorsForRow(row, rowIndex);
          if (frontend.length === 0 && backend.length === 0) return null;
          // Use newline to separate errors for better word wrap in spreadsheet
          const allErrors = [...frontend, ...backend]
            .join('\n')
            .replace(/"/g, "'");
          return [
            row.schemaId,
            row.item,
            row.price,
            row.lineNumber,
            `"${allErrors}"`,
          ].join(',');
        })
        .filter(Boolean),
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'all_invalid_rows.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Compute valid and invalid counts
  const validCount =
    parsedData && parsedData.rows
      ? parsedData.rows.filter((row, idx) => !isRowInvalid(row, idx)).length
      : 0;
  const invalidCount =
    parsedData && parsedData.rows
      ? parsedData.rows.filter((row, idx) => isRowInvalid(row, idx)).length
      : 0;
  const allValid = invalidCount === 0;

  // Compute total error count from rowErrorsFromBackend
  const totalErrorCount = rowErrorsFromBackend.reduce(
    (sum, row) => sum + (row.errors ? row.errors.length : 0),
    0,
  );

  const willUpdateCount =
    parsedData && parsedData.rows
      ? parsedData.rows.filter(
          (row, idx) =>
            !isRowInvalid(row, idx) && changeStatus[idx] === 'Will Update',
        ).length
      : 0;

  const getButtonText = () => {
    if (willUpdateCount > 0) {
      return `Submit (${willUpdateCount} updates)`;
    } else if (
      parsedData &&
      parsedData.rows &&
      parsedData.rows.filter((row, idx) => !isRowInvalid(row, idx)).length >
        0 &&
      Object.keys(changeStatus).length > 0
    ) {
      return 'No Updates Needed';
    } else {
      return 'Submit';
    }
  };

  const getStatusBadgeClass = (isInvalid, rowIndex) => {
    return isInvalid
      ? styles.changeStatusInvalid
      : changeStatus[rowIndex] === 'Will Update'
      ? styles.changeStatusWillUpdate
      : styles.changeStatusNoChange;
  };

  const getStatusBadgeText = (isInvalid, rowIndex) => {
    return isInvalid
      ? 'Invalid'
      : changeStatus[rowIndex] === 'Will Update'
      ? 'Will Update'
      : changeStatus[rowIndex] === 'No Change'
      ? 'No Change'
      : '-';
  };

  return (
    <main className={styles.container}>
      {/* Feedback at the top for immediate visibility */}
      {feedback ? (
        <ValidationFeedback data={feedback.data} type={feedback.type} />
      ) : null}

      <h1>Base Price CSV Upload</h1>
      <form className={styles.form} onSubmit={submitHandler}>
        <div className={styles.formWrapper}>
          <div className={styles.flexColumn}>
            <label htmlFor="csvFileInput" className={styles.label}>
              Upload CSV file
            </label>
            <span className={styles.fileInfo}>
              {' '}
              File must contain columns: <b>schemaId</b>, <b>item</b>,{' '}
              <b>price</b>
            </span>
            <input
              id="csvFileInput"
              type="file"
              accept=".csv"
              onChange={changeFileHandler}
              ref={csvFileRef}
              style={{ cursor: 'pointer' }}
            />
          </div>
          {invalidCount > 0 ? (
            <button
              className={styles.button}
              onClick={downloadAllInvalidRows}
              type="button"
            >
              Download All Invalid Rows
            </button>
          ) : (
            <button
              className={styles.button}
              disabled={
                errors.length ||
                !csvFile ||
                loading ||
                fetchingPrices ||
                rowErrorsFromBackend.length > 0 ||
                validating
              }
              type="submit"
            >
              {loading
                ? 'Loading...'
                : fetchingPrices
                ? 'Loading Prices...'
                : getButtonText()}
            </button>
          )}
        </div>
      </form>

      {loading && (
        <div className={styles.progressBarContainer}>
          <ProgressBar now={uploadProgress} label={`${uploadProgress}%`} />
        </div>
      )}

      {fetchingPrices && (
        <div className={styles.progressBarContainer}>
          <ProgressBar
            animated
            label="Fetching current prices for comparison..."
          />
        </div>
      )}

      {validating && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            margin: '48px 0',
          }}
        >
          <div className={styles.validatingMessage}>
            Validating your upload, please wait...
          </div>
        </div>
      )}

      {/* CSV Data Table */}
      {!validating && parsedData && (
        <div className={styles.tableContainer}>
          <h3>CSV Data Preview</h3>
          {parsedData && parsedData.rows && (
            <div className={styles.summary}>
              <p>
                <strong>Summary:</strong> {parsedData.rows.length} total rows,{' '}
                <span
                  style={{
                    color: allValid ? '#28a745' : 'black',
                    fontWeight: 600,
                  }}
                >
                  {validCount} valid
                </span>
                ,{' '}
                <span
                  style={{
                    color: invalidCount > 0 ? '#dc3545' : 'black',
                    fontWeight: 600,
                  }}
                >
                  {invalidCount} invalid
                </span>
                {Object.keys(changeStatus).length > 0 && (
                  <>
                    ,{' '}
                    {
                      parsedData.rows.filter(
                        (row, idx) =>
                          !isRowInvalid(row, idx) &&
                          changeStatus[idx] === 'Will Update',
                      ).length
                    }{' '}
                    will update,{' '}
                    {
                      parsedData.rows.filter(
                        (row, idx) =>
                          !isRowInvalid(row, idx) &&
                          changeStatus[idx] === 'No Change',
                      ).length
                    }{' '}
                    no change
                  </>
                )}
              </p>
            </div>
          )}
          <div className={styles.tableWrapper}>
            <Table>
              <thead>
                <tr>
                  <th>Line #</th>
                  <th>Status</th>
                  <th className="schemaCol">schemaId</th>
                  <th className="itemCol">item</th>
                  <th className="priceCol">price</th>
                  <th>Change Status</th>
                  <th>Validation</th>
                </tr>
              </thead>
              <tbody>
                {parsedData.rows.map((row, rowIndex) => {
                  const { frontend, backend } = getAllErrorsForRow(
                    row,
                    rowIndex,
                  );
                  const isInvalid = isRowInvalid(row, rowIndex);
                  const rowClass = isInvalid
                    ? styles.errorRow
                    : styles.validRow;
                  return (
                    <tr key={rowIndex} className={rowClass}>
                      <td>{row.lineNumber}</td>
                      <td>
                        {isInvalid ? (
                          <Badge bg="danger">Invalid</Badge>
                        ) : (
                          <Badge bg="success">Valid</Badge>
                        )}
                      </td>
                      <td className="schemaCol">{row.schemaId}</td>
                      <td className="itemCol">{row.item}</td>
                      <td className="priceCol">{row.price}</td>
                      <td>
                        <span
                          className={
                            styles.changeStatusBadge +
                            ' ' +
                            getStatusBadgeClass(isInvalid, rowIndex)
                          }
                        >
                          {getStatusBadgeText(isInvalid, rowIndex)}
                        </span>
                      </td>
                      <td>
                        {[...frontend, ...backend].length > 0 && (
                          <ul className={styles.errorList}>
                            {[...frontend, ...backend].map(
                              (error, errorIndex) => (
                                <li
                                  key={errorIndex}
                                  className={styles.errorItem}
                                  style={{ color: '#dc3545', fontWeight: 600 }}
                                >
                                  {error}
                                </li>
                              ),
                            )}
                          </ul>
                        )}
                        {frontend.length === 0 && backend.length === 0 && (
                          <span className={styles.validText}>✓ Valid</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </div>
      )}
    </main>
  );
};

export default ItemPricesCSVTool;
